/**
 * Hands a finished print PDF to the iPhone / iPad share sheet.
 *
 * Why not print it in the page: iOS prints through UIKit, which keeps the page
 * count of its first pagination pass and draws only that many pages, so a long
 * page print silently loses its tail (the chef packet dropped its last school).
 * A PDF in a frame is no way around it either: iOS draws a framed PDF as an
 * image of its first page, and printing the frame prints that one page.
 *
 * Shared as a FILE, the PDF reaches the share sheet as a real document (WebKit
 * writes it to a file URL and excludes only SharePlay from the sheet), so the
 * sheet offers Print, which prints the file's own pages, and Save to Files.
 *
 * `deliver()` must run inside the tap: sharing needs the user's activation.
 */

export interface PdfShareSheetDeps {
  share?: (data: ShareData) => Promise<void>
  canShare?: (data: ShareData) => boolean
  /** Fallback for a browser that cannot share files: open the PDF in its own viewer. */
  open: (url: string) => void
  createObjectURL: (blob: Blob) => string
  revokeObjectURL: (url: string) => void
}

function browserDeps(): PdfShareSheetDeps {
  const nav = globalThis.navigator
  return {
    share: typeof nav?.share === 'function' ? (data) => nav.share(data) : undefined,
    canShare: typeof nav?.canShare === 'function' ? (data) => nav.canShare(data) : undefined,
    open: (url) => {
      window.open(url, '_blank')
    },
    createObjectURL: (blob) => URL.createObjectURL(blob),
    revokeObjectURL: (url) => URL.revokeObjectURL(url),
  }
}

/** Path separators, characters no file system allows, and control characters. */
// eslint-disable-next-line no-control-regex
const UNSAFE_FILE_NAME_CHARS = /[\\/:*?"<>|\u0000-\u001f]+/g

/** The packet's file name: the print title with path and reserved characters replaced ("Mon 09/28" → "Mon 09-28"). */
export function pdfFileName(title: string): string {
  const base = title.replace(UNSAFE_FILE_NAME_CHARS, '-').replace(/\s+/g, ' ').trim()
  return `${base || 'Ordo_Print'}.pdf`
}

/** A dismissed share sheet rejects with an AbortError; that is the user's choice, not a failure. */
export function isShareDismissal(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError'
}

export class PdfShareSheet {
  private url: string | null = null

  constructor(private readonly deps: PdfShareSheetDeps = browserDeps()) {}

  /**
   * Offer `pdf` as `fileName` — call it inside the tap. Resolves when the sheet
   * finishes; rejects with an AbortError when the user dismisses it.
   */
  deliver(pdf: Blob, fileName: string): Promise<void> {
    const data: ShareData = { files: [new File([pdf], fileName, { type: 'application/pdf' })] }
    const { share, canShare } = this.deps
    if (share && canShare?.(data)) {
      return share(data)
    }
    // No file sharing (iOS before 15): open the PDF in the browser's own
    // viewer, which prints and saves the whole file.
    this.dispose()
    this.url = this.deps.createObjectURL(pdf)
    this.deps.open(this.url)
    return Promise.resolve()
  }

  /** Revoke the fallback's PDF URL. */
  dispose(): void {
    if (this.url) {
      this.deps.revokeObjectURL(this.url)
      this.url = null
    }
  }
}
